import { MessageBarType } from '@fluentui/react';
import { Button, Dropdown, MessageBar, TextField, useTheme } from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';

import { CreateTrustedRolesResponse } from '../../admin-center/gen/trusted_roles/trusted_roles_pb';
import { useAdminCenterService } from '../../admin-center/hooks';
import { useEnv, useUser } from '../../utils/hooks';
import { ResponseMessage } from './common/types';
import { buildUserContext } from './common/utils';
import {
  createButtonStyles,
  createGroupFormStyles,
  createGroupSectionStyles,
  messageBarContainerStyles,
  sectionHeaderStyles,
} from './components/CustomGroupManagement/CustomGroupManagement.styles';
import TrustedRolesList from './components/TrustedRoleManagement/RoleList/RoleList';
import PageWrapper from './PageWrapper';

const parseRoleNames = (roleName: string): string[] => {
  return roleName.includes(',')
    ? roleName
        .split(',')
        .map((r) => r.trim())
        .filter((r) => r.length > 0)
    : [roleName.trim()];
};

export default function TrustedRoleManagement() {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const env = useEnv();

  // Check if H2OGPTE is available in the environment
  const h2oGpteAvailable = env?.menu?.externalMenuItems?.some(
    (item) => item.key === 'Enterpriseh2oGPTe' || item.name?.includes('h2oGPTe')
  );

  // Check if H2OGPTE is enabled: h2oGPTE is available in the environment
  const h2oGpteEnabled = Boolean(h2oGpteAvailable);

  // Conditionally include h2oGPTe option based on environment availability
  const productOptions = [
    {
      key: 'dai',
      text: 'DAI',
    },
    ...(h2oGpteEnabled
      ? [
          {
            key: 'h2ogpte',
            text: 'h2oGPTe',
          },
        ]
      : []),
  ];
  const [roleName, setRoleName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<string>('dai');
  const [responseMessages, setResponseMessages] = useState<ResponseMessage[]>([]);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isFormValid = useCallback(() => {
    const roleNames = parseRoleNames(roleName);

    if (roleNames.length === 0 || roleNames.some((r) => r.length === 0)) {
      return false;
    }

    return !roleNames.some((role) => role.includes(' '));
  }, [roleName]);

  const getFieldErrorMessage = useCallback(() => {
    const roleNames = parseRoleNames(roleName);

    if (roleNames.length === 0) {
      return undefined;
    }

    if (roleNames.some((role) => role.includes(' '))) {
      return 'Role names cannot contain spaces';
    }

    return undefined;
  }, [roleName]);

  const handleCreateRoleChange = (
    _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setRoleName(newValue || '');
  };

  const handleProductChange = (_event: React.FormEvent<HTMLDivElement>, item: any) => {
    setSelectedProduct(item.key);
  };

  const formatCreateRolesResponse = (response: CreateTrustedRolesResponse): ResponseMessage[] => {
    const messages: ResponseMessage[] = [];

    // Success roles
    if (response.successRoles && response.successRoles.length > 0) {
      const roleNames = response.successRoles.map((rs) => rs.role).join(', ');
      messages.push({
        type: MessageBarType.success,
        content: `Successfully added: ${roleNames}`,
      });
    }

    // Skipped roles
    if (response.skippedRoles && response.skippedRoles.length > 0) {
      const roleDetails = response.skippedRoles.map((rs) => `${rs.role} (${rs.message})`).join(', ');
      messages.push({
        type: MessageBarType.warning,
        content: `Skipped: ${roleDetails}`,
      });
    }

    // Invalid roles
    if (response.invalidRoles && response.invalidRoles.length > 0) {
      const roleDetails = response.invalidRoles.map((rs) => `${rs.role} (${rs.message})`).join(', ');
      messages.push({
        type: MessageBarType.error,
        content: `Invalid: ${roleDetails}`,
      });
    }

    // Error roles
    if (response.errorRoles && response.errorRoles.length > 0) {
      const roleDetails = response.errorRoles.map((rs) => `${rs.role} (${rs.message})`).join(', ');
      messages.push({
        type: MessageBarType.error,
        content: `Error: ${roleDetails}`,
      });
    }

    return messages;
  };

  const handleCreateClick = useCallback(async () => {
    if (!isFormValid()) return;

    try {
      const userContext = await buildUserContext(user);
      const roleNames = parseRoleNames(roleName);
      const request = {
        product: selectedProduct,
        roleNames: roleNames,
        userContext: userContext,
      };
      const response = await service.createTrustedRolesRequest(request);
      const messages = formatCreateRolesResponse(response);

      if (messages.length > 0) {
        setResponseMessages(messages);
        // Refresh the list if any roles were successfully added
        if (response.successRoles && response.successRoles.length > 0) {
          setRefreshKey((prev) => prev + 1);
        }
      } else {
        setResponseMessages([
          {
            type: MessageBarType.error,
            content: 'Failed to create trusted role',
          },
        ]);
      }
      setRoleName('');
    } catch (error) {
      setResponseMessages([
        {
          type: MessageBarType.error,
          content: `Network error: ${error}`,
        },
      ]);
    } finally {
      setIsRequestLoading(false);
    }
  }, [roleName, isFormValid, isRequestLoading, user, selectedProduct, service]);

  const handleCreateButtonClick = () => {
    setIsRequestLoading(true);
    handleCreateClick();
  };

  return (
    <PageWrapper>
      <div style={createGroupSectionStyles(theme)}>
        <div style={sectionHeaderStyles(theme)}>
          <h2 style={{ margin: 0, color: theme.palette?.gray750 }}>Create Trusted Role</h2>
          <p style={{ color: theme.palette?.gray500, margin: '8px 0 0 0', fontSize: '14px' }}>
            Add new trusted roles to your organization.
          </p>
        </div>

        <div style={createGroupFormStyles()}>
          <TextField
            label="Role ARN"
            value={roleName}
            onChange={handleCreateRoleChange}
            errorMessage={getFieldErrorMessage()}
            styles={{
              root: {
                flex: 1,
                minHeight: '76px',
              },
              fieldGroup: { borderRadius: '4px' },
            }}
          />
          <Dropdown
            label="Product"
            selectedKey={selectedProduct}
            options={productOptions}
            onChange={handleProductChange}
            styles={{
              root: {
                flex: 1,
              },
              dropdown: { borderRadius: '4px' },
            }}
          />
          <Button
            styles={createButtonStyles}
            text={isRequestLoading ? 'Creating...' : 'Create Role'}
            onClick={handleCreateButtonClick}
            disabled={!isFormValid() || isRequestLoading}
          />
        </div>

        {responseMessages.length > 0 && (
          <div style={messageBarContainerStyles()}>
            {responseMessages.map((msg, index) => (
              <MessageBar
                key={index}
                timeout={6000}
                onDismiss={() => {
                  setResponseMessages((prev) => prev.filter((_, i) => i !== index));
                }}
                messageBarType={msg.type}
                styles={{ root: { marginBottom: index < responseMessages.length - 1 ? '8px' : '0' } }}
              >
                {msg.content}
              </MessageBar>
            ))}
          </div>
        )}
      </div>
      <TrustedRolesList refreshKey={refreshKey} onMessage={(msgs) => setResponseMessages(msgs)} />
    </PageWrapper>
  );
}
